/**
 * VTT Parser for extracting chapter information from VTT files
 */

import { fetch as apiFetch, TYPE_GET } from '../../api-client/index'

/**
 * Parse VTT content and extract chapters
 * @param {string} vttContent - The raw VTT file content
 * @returns {Array} Array of chapter objects with time and title
 */
export function parseVTT (vttContent) {
  const chapters = []
  const lines = vttContent.split('\n')

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i].trim()

    // Skip empty lines and WEBVTT header
    if (!line || line === 'WEBVTT' || line.startsWith('NOTE')) {
      // eslint-disable-next-line no-continue
      continue
    }

    // Check if this line contains a timestamp
    const timeMatch = line.match(/^(\d{2}:\d{2}:\d{2}\.\d{3}) --> (\d{2}:\d{2}:\d{2}\.\d{3})/)
    if (timeMatch) {
      const startTime = timeMatch[1]
      const endTime = timeMatch[2]

      // Look for the title in the next line
      const nextLine = lines[i + 1] ? lines[i + 1].trim() : ''
      if (nextLine && !nextLine.match(/^\d{2}:\d{2}:\d{2}\.\d{3}/)) {
        // Convert timestamp to seconds
        const timeInSeconds = timeToSeconds(startTime)
        chapters.push({
          time: timeInSeconds,
          title: nextLine,
          startTime,
          endTime,
        })
      }
    }
  }

  return chapters
}

/**
 * Convert VTT timestamp to seconds
 * @param {string} timestamp - VTT timestamp (HH:MM:SS.mmm)
 * @returns {number} Time in seconds
 */
function timeToSeconds (timestamp) {
  const parts = timestamp.split(':')
  const hours = parseInt(parts[0], 10)
  const minutes = parseInt(parts[1], 10)
  const seconds = parseFloat(parts[2])

  return hours * 3600 + minutes * 60 + seconds
}

/**
 * Fetch VTT file content from URL
 * @param {string} url - VTT file URL
 * @param {Object} auth - Authentication object with JWT
 * @returns {Promise<string>} VTT file content
 */
export async function fetchVTT (url, auth = null) {
  try {
    const response = await apiFetch(url, TYPE_GET, null, {
      auth,
      responseType: 'text',
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.text
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching VTT file:', error)
    throw error
  }
}

/**
 * Fetch and parse VTT file to get chapters
 * @param {string} url - VTT file URL
 * @param {Object} auth - Authentication object with JWT
 * @returns {Promise<Array>} Array of chapter objects
 */
export async function getChaptersFromVTT (url, auth = null) {
  try {
    const vttContent = await fetchVTT(url, auth)
    return parseVTT(vttContent)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting chapters from VTT:', error)
    return []
  }
}
